import { extendTheme, useTheme as useTheme$1 } from '@chakra-ui/react';
import { defineStyleConfig, defineStyle, createMultiStyleConfigHelpers } from '@chakra-ui/styled-system';
import chakraTheme, { theme as theme$1 } from '@chakra-ui/theme';
import { Tabs } from '@chakra-ui/theme/components';
import { cssVar } from '@chakra-ui/theme-tools';
import { drawerAnatomy, menuAnatomy } from '@chakra-ui/anatomy';

const alertStyles = {
  variants: {
    "cart-action": {
      container: {
        backgroundColor: "gray-opacity.700",
        color: "white.50",
        paddingX: "6",
        paddingY: "3"
      },
      description: {
        fontWeight: "semibold"
      },
      icon: {
        /**
         * "display: 'none'" will not work here, so this hack will have to do.
         */
        margin: "0",
        padding: "0",
        width: "0"
      }
    }
  },
  defaultProps: {
    variant: "subtle"
  }
};

const alertDialogStyle = {
  baseStyle: {
    background: "background"
  }
};

const badgeStyle = {
  baseStyle: ({ colorMode }) => ({
    textTransform: "uppercase",
    fontWeight: "bold",
    letterSpacing: "0.04em",
    padding: "0px 4px",
    borderRadius: "2px",
    fontSize: "12px"
  }),
  variants: {
    solid: () => ({
      bg: "primary"
    }),
    outline: () => ({
      bg: "white",
      color: "primary",
      borderColor: "primary"
    }),
    subtle: () => ({
      bg: "highlight",
      color: "primary"
    })
  }
};

const spacing = {
  ...chakraTheme.space,
  px: "1px",
  0: "0rem",
  0.5: "0.125rem",
  //2px
  1: "0.25rem",
  //4px
  2: "0.5rem",
  //8px
  3: "0.75rem",
  //12px
  4: "1rem",
  //16px
  5: "1.25rem",
  //20px
  6: "1.5rem",
  //24px
  7: "1.75rem",
  //28px
  8: "2rem",
  //32px
  9: "2.25rem",
  //36px
  10: "2.5rem",
  //40px
  12: "3rem",
  //48px
  14: "3.5rem",
  //56px
  15: "3.75rem",
  //60px
  16: "4rem",
  //64px
  20: "5rem",
  //80px
  24: "6rem",
  //96px
  28: "7rem",
  //112px
  32: "8rem",
  //128px
  36: "9rem",
  //144px
  40: "10rem",
  //160px
  44: "11rem",
  //176px
  // 45: '11.25rem', //180px - TBD inclusion in Figma design
  48: "12rem",
  //192px
  52: "13rem",
  //208px
  56: "14rem",
  //224px
  60: "15rem",
  //240px
  64: "16rem",
  //256px
  72: "18rem",
  //288px
  80: "20rem",
  //320px
  96: "24rem"
  //384px
};

const layerStyles = {
  focusKeyboard: {
    outlineStyle: "solid",
    outlineWidth: "3px",
    outlineColor: "gray.500"
  },
  focusKeyboardImg: {
    outlineStyle: "solid",
    outlineWidth: "3px",
    outlineColor: "black",
    outlineOffset: "3px",
    opacity: 0.5,
    cursor: "pointer"
  }
};

const Button = defineStyleConfig({
  baseStyle: {
    fontWeight: "semibold",
    letterSpacing: "wide",
    borderRadius: "base",
    width: "fit-content",
    _disabled: {
      opacity: 1
    },
    _focus: layerStyles.focusKeyboard
  },
  sizes: {
    xs: {
      fontSize: "sm",
      padding: "3px 8px",
      lineHeight: "17.5px"
    },
    sm: {
      fontSize: "md",
      padding: "6px 12px",
      lineHeight: "shorter"
    },
    md: {
      fontSize: "md",
      padding: "10px 16px",
      lineHeight: "shorter"
    },
    lg: {
      fontSize: "lg",
      padding: "12.5px 24px",
      lineHeight: "short"
    }
  },
  variants: {
    primary: {
      color: "white",
      backgroundColor: "primary.500",
      _hover: {
        backgroundColor: "primary.600"
      },
      _active: {
        backgroundColor: "primary.700"
      },
      _disabled: {
        backgroundColor: "gray.100",
        color: "gray.400"
      }
    },
    secondary: {
      color: "white",
      backgroundColor: "gray.500",
      _hover: {
        backgroundColor: "gray.600"
      },
      _active: {
        backgroundColor: "gray.700"
      },
      _disabled: {
        backgroundColor: "gray.100",
        color: "gray.400"
      }
    },
    outline: {
      color: "gray.700",
      backgroundColor: "white",
      border: "1px",
      borderColor: "gray.200",
      _hover: {
        borderColor: "gray.500",
        backgroundColor: "white"
      },
      _active: {
        borderColor: "gray.700",
        backgroundColor: "white"
      },
      _disabled: {
        borderColor: "gray.300",
        color: "gray.300",
        backgroundColor: "white"
      }
    },
    "ghost-primary": {
      color: "primary.500",
      backgroundColor: "transparent",
      _hover: {
        color: "gray.700",
        backgroundColor: "transparent"
      },
      _active: {
        color: "primary.700",
        backgroundColor: "transparent"
      },
      _disabled: {
        color: "gray.300",
        backgroundColor: "transparent"
      }
    },
    "ghost-secondary": {
      color: "gray.700",
      backgroundColor: "transparent",
      _hover: {
        color: "primary.500",
        backgroundColor: "transparent"
      },
      _active: {
        color: "gray.900",
        backgroundColor: "transparent"
      },
      _disabled: {
        color: "gray.300",
        backgroundColor: "transparent"
      }
    },
    "ghost-light": {
      color: "gray.100",
      backgroundColor: "transparent",
      _hover: {
        color: "white.600",
        backgroundColor: "transparent",
        textDecoration: "underline",
        textUnderlineOffset: spacing[1]
      },
      _active: {
        color: "warm-white",
        backgroundColor: "transparent"
      },
      _disabled: {
        color: "gray.300",
        backgroundColor: "transparent"
      }
    },
    "link-primary": {
      color: "primary.500",
      backgroundColor: "transparent",
      textDecoration: "underline",
      textUnderlineOffset: spacing[1],
      _hover: {
        color: "gray.700",
        backgroundColor: "transparent"
      },
      _active: {
        color: "primary.700",
        backgroundColor: "transparent"
      },
      _disabled: {
        color: "gray.300",
        backgroundColor: "transparent"
      }
    },
    "link-secondary": {
      color: "gray.700",
      backgroundColor: "transparent",
      textDecoration: "underline",
      textUnderlineOffset: spacing[1],
      _hover: {
        color: "primary.500",
        backgroundColor: "transparent"
      },
      _active: {
        color: "gray.900",
        backgroundColor: "transparent"
      },
      _disabled: {
        color: "gray.300",
        backgroundColor: "transparent"
      }
    },
    "link-header": {
      fontSize: "sm",
      color: "text",
      textDecoration: "none",
      fontWeight: "light",
      lineHeight: "shorter",
      height: 10,
      marginY: 0.5,
      padding: 0,
      textAlign: "left",
      borderRadius: "none",
      width: "full",
      a: {
        _hover: {
          backgroundColor: "#eee"
        }
      },
      _focusVisible: {
        boxShadow: "outline"
      }
    }
  },
  defaultProps: {
    size: "md",
    variant: "primary"
  }
});

const cardStyles = {
  baseStyle: {
    container: {
      backgroundColor: "white.50"
    }
  },
  variants: {
    "blog-card": {
      ...theme$1.components.Card.variants?.elevated,
      body: {
        paddingBottom: "3"
      },
      container: {
        ...theme$1.components.Card.variants?.elevated.container,
        width: "100%"
      },
      header: {
        padding: "0"
      },
      footer: {
        paddingTop: "3"
      }
    },
    "checkout-detail-item": {
      ...theme$1.components.Card.variants?.elevated,
      body: {
        marginTop: "2",
        padding: "0"
      },
      container: {
        ...theme$1.components.Card.variants?.elevated.container,
        paddingX: { base: "6", lg: "12" },
        paddingY: { base: "6", lg: "8" }
      },
      header: {
        margin: "0",
        marginBottom: "2",
        padding: "0"
      }
    }
  }
};

const disabledStyle = {
  backgroundColor: "gray.100",
  borderColor: "gray.100"
};
const checkboxStyle = {
  baseStyle: {
    control: {
      borderColor: "gray.700",
      borderRadius: "sm",
      borderWidth: "2px",
      _checked: {
        _disabled: disabledStyle
      },
      _disabled: disabledStyle,
      _invalid: {
        borderColor: "red.500"
      }
    }
  },
  defaultProps: {
    colorScheme: "teal"
  }
};

const dividerStyles = {
  // Styles for the base style
  baseStyle: {
    borderColor: "gray.300"
  },
  variants: {
    solid: {
      borderColor: "gray.300"
    },
    dashed: {
      borderColor: "gray.300"
    }
  }
};

const fonts = {
  ...chakraTheme.fonts,
  body: "var(--font-figtree) !important",
  heading: "var(--font-zillaSlab) !important",
  "heading-serif": `'Parry Pro', serif !important`
};
const fontSizes = {
  ...chakraTheme.fontSizes,
  xs: "0.75rem",
  // 12px
  sm: "0.875rem",
  // 14px
  md: "1rem",
  // 16px
  lg: "1.125rem",
  // 18px
  xl: "1.25rem",
  // 20px
  "2xl": "1.5rem",
  // 24px
  "3xl": "1.875rem",
  // 30px
  "4xl": "2.25rem",
  // 36px
  "5xl": "3rem",
  // 48px
  "6xl": "3.75rem",
  // 60px
  "7xl": "4.5rem",
  // 72px
  "8xl": "6rem",
  // 96px
  "9xl": "8rem"
  // 128px
};
const fontWeights = {
  ...chakraTheme.fontWeights,
  light: 300,
  normal: 400,
  semibold: 600,
  bold: 700
};
const lineHeights = {
  ...chakraTheme.lineHeights,
  normal: 1.375,
  // 22px
  none: 1,
  // 16px
  shorter: 1.25,
  // 20px
  short: 1.375,
  // 22px
  base: 1.5,
  // 24px
  tall: 1.625,
  // 26px
  taller: 2
  // 32px
};
const letterSpacings = {
  ...chakraTheme.letterSpacings,
  tighter: "-0.05em",
  // -0.8px
  tight: "-0.025em",
  // -0.4px
  normal: 0,
  wide: "0.025em",
  // 0.4px
  wider: "0.05em",
  // 0.8px
  widest: "0.1em"
  // 1.6px
};
const typography = {
  fonts,
  fontSizes,
  fontWeights,
  letterSpacings,
  lineHeights
};

const palette = {
  /* as of 6/4/2024, primary = teal, secondary = yellow, tertiary = earth
   * success = green, danger = red, warning = yellow
   *
   * reference this figma file for up to date colors (Design System Pattern Library -> Colors):
   * https://www.figma.com/design/xnpOmVeADqo4HcZav2nBwK/Design-System-Pattern-Library?node-id=1-13
   */
  primary: {
    // teal
    50: "#EFF6F6",
    100: "#DAEAEA",
    200: "#9EC6C6",
    300: "#7AAAAC",
    400: "#568E92",
    500: "#337179",
    600: "#1F6169",
    700: "#0D5257",
    800: "#074247",
    900: "#032C2F"
  },
  secondary: {
    // yellow
    50: "#FCF7E8",
    100: "#F8EFCD",
    200: "#F5E1A4",
    300: "#F3D792",
    400: "#F1CD80",
    500: "#EFC06E",
    600: "#CBA052",
    700: "#b58500",
    800: "#8f6a00",
    900: "#684d00"
  },
  tertiary: {
    // earth
    50: "#FBF1E9",
    100: "#F3CFB3",
    200: "#EBB38C",
    300: "#E59E6D",
    400: "#D0835B",
    500: "#B86748",
    600: "#A0593E",
    700: "#7C442F",
    800: "#713F2A",
    900: "#5B3122"
  },
  success: {
    // green
    50: "#F2FCF9",
    100: "#A7E6D7",
    200: "#6ECEB2",
    300: "#27B093",
    400: "#219884",
    500: "#1C8172",
    600: "#1A7667",
    700: "#176353",
    800: "#124A3B",
    900: "#0E3C32"
  },
  danger: {
    // red
    50: "#FBE9EA",
    100: "#EBC1C2",
    200: "#DC989B",
    300: "#CD6F74",
    400: "#BE464D",
    500: "#AF1D26",
    600: "#921821",
    700: "#73131A",
    800: "#540E13",
    900: "#35090C"
  },
  warning: {
    // yellow
    100: "#F8EFCD",
    200: "#F5E1A4",
    300: "#F3D792",
    400: "#F1CD80",
    500: "#EFC06E",
    600: "#CBA052",
    700: "#b58500",
    800: "#8f6a00",
    900: "#684d00"
  },
  info: {
    100: "#DCF4FE",
    200: "#B9E6FE",
    300: "#96D4FE",
    400: "#7BC2FD",
    500: "#50A6FC",
    600: "#3A81D8",
    700: "#2860B5",
    800: "#194392",
    900: "#0F2F78"
  },
  shading: {
    100: "#F4F4F4",
    200: "#EAEAEA",
    300: "#C0C0C0",
    400: "#828282",
    500: "#404040",
    600: "#303030",
    700: "#222222",
    800: "#1B1B1B",
    900: "#111111"
  },
  // #endregion
  // #region Spoonflower theme
  gray: {
    50: "#F1F3F3",
    100: "#D3D4D7",
    200: "#B3B7BA",
    300: "#939A9D",
    400: "#737D80",
    500: "#536063",
    600: "#424C4D",
    700: "#303738",
    800: "#1E2223",
    900: "#0C0D0E"
  },
  /**
   * You'll notice that this is this exactly the same as "gray".
   * This is because we want to use the "gray" color scheme in buttons
   * without Chakra's alternate styling for it.
   *
   * Why "granite"? It's the color name for the "500" shade.
   * https://www.color-name.com/hex/61696b
   */
  granite: {
    50: "#F1F3F3",
    100: "#D3D4D7",
    200: "#B3B7BA",
    300: "#939A9D",
    400: "#737D80",
    500: "#536063",
    600: "#424C4D",
    700: "#303738",
    800: "#1E2223",
    900: "#0C0D0E"
  },
  teal: {
    // use primary
    50: "#EFF6F6",
    100: "#DAEAEA",
    200: "#9EC6C6",
    300: "#7AAAAC",
    400: "#568E92",
    500: "#337179",
    600: "#1F6169",
    700: "#0D5257",
    800: "#074247",
    900: "#032C2F"
  },
  yellow: {
    50: "#FCF7E8",
    // use secondary
    100: "#F8EFCD",
    // use warning
    200: "#F5E1A4",
    300: "#F3D792",
    400: "#F1CD80",
    500: "#EFC06E",
    600: "#CBA052",
    700: "#B58500",
    800: "#8F6A00",
    900: "#684D00"
  },
  earth: {
    // use tertiary
    50: "#FBF1E9",
    100: "#F3CFB3",
    200: "#EBB38C",
    300: "#E59E6D",
    400: "#D0835B",
    500: "#B86748",
    600: "#A0593E",
    700: "#7C442F",
    800: "#713F2A",
    900: "#5B3122"
  },
  green: {
    // use success
    50: "#F4FDF0",
    100: "#E2FAD8",
    200: "#C0F6B3",
    300: "#91E688",
    400: "#28943B",
    500: "#1B7C35",
    600: "#00641D",
    700: "#11642F",
    800: "#0A532A",
    900: "#0D4B31"
  },
  red: {
    // use danger
    50: "#FFF7F4",
    100: "#FDE0D4",
    200: "#FCBBAA",
    300: "#F68C7E",
    400: "#ED605D",
    500: "#C21F38",
    600: "#B3112C",
    700: "#A21538",
    800: "#830D35",
    900: "#6C0832"
  },
  black: "#000000",
  white: "#FFFFFF",
  "warm-white": "#FEFEFE",
  "gray-opacity": {
    50: "rgba(0, 0, 0, 0.04)",
    100: "rgba(0, 0, 0, 0.06)",
    200: "rgba(0, 0, 0, 0.08)",
    300: "rgba(0, 0, 0, 0.16)",
    400: "rgba(0, 0, 0, 0.24)",
    500: "rgba(0, 0, 0, 0.36)",
    600: "rgba(0, 0, 0, 0.48)",
    700: "rgba(0, 0, 0, 0.64)",
    800: "rgba(0, 0, 0, 0.80)",
    900: "rgba(0, 0, 0, 0.92)"
  },
  "white-opacity": {
    50: "rgba(255, 255, 255, 0.04)",
    100: "rgba(255, 255, 255, 0.06)",
    200: "rgba(255, 255, 255, 0.08)",
    300: "rgba(255, 255, 255, 0.16)",
    400: "rgba(255, 255, 255, 0.24)",
    500: "rgba(255, 255, 255, 0.36)",
    600: "rgba(255, 255, 255, 0.48)",
    700: "rgba(255, 255, 255, 0.64)",
    800: "rgba(255, 255, 255, 0.80)",
    900: "rgba(255, 255, 255, 0.92)"
  },
  "border-color": "#EFEFEF"
};
const colorTokens = {
  // #region Orium theme
  // info
  "info-light": palette.info["100"],
  "info-med": palette.info["500"],
  "info-dark": palette.info["900"],
  // success
  "success-light": palette.success["100"],
  "success-med": palette.success["500"],
  "success-dark": palette.success["900"],
  // warning
  "warning-light": palette.warning["100"],
  "warning-med": palette.warning["500"],
  "warning-dark": palette.warning["700"],
  // danger
  "danger-light": palette.danger["100"],
  "danger-med": palette.danger["500"],
  "danger-dark": palette.danger["900"]
  // #endregion
};
const paletteTokens = {
  colors: {
    // #region Orium theme
    light: {
      background: palette["warm-white"],
      text: palette.black,
      //Body foreground color
      "text-muted": palette.shading["400"],
      // Body foreground color (muted) for alternative styling
      // TODO update once we have CMS controlled theme
      primary: palette.primary["500"],
      // Primary color for links, buttons, etc.
      secondary: palette.secondary["700"],
      //A secondary brand color for alternative styling
      highlight: palette.secondary["500"],
      //A highlight color for emphasizing UI
      muted: palette.tertiary["500"],
      accent: palette.tertiary["700"]
      //A contrast color for emphasizing UI
    },
    dark: {
      background: palette.black,
      text: palette.gray["900"],
      //Body foreground color
      "text-muted": palette.shading["300"],
      // Body foreground color (muted) for alternative styling
      // TODO update once we have CMS controlled theme
      primary: palette.primary["600"],
      // Primary color for links, buttons, etc
      secondary: palette.primary["400"],
      //A secondary brand color for alternative styling
      highlight: palette.secondary["500"],
      //A highlight color for emphasizing UI
      muted: palette.tertiary["500"],
      accent: palette.tertiary["700"]
      //A contrast color for emphasizing UI
    },
    // #endregion
    // #region Spoonflower theme
    link: {
      teal: {
        base: palette.teal[500],
        hover: palette.teal[700]
      }
    },
    text: {
      disabled: palette.gray[400],
      primary: {
        base: palette.gray[900],
        hover: palette.black
      },
      secondary: {
        base: palette.gray[700],
        hover: palette.gray[900]
      },
      tertiary: {
        base: palette.gray[500],
        hover: palette.gray[700]
      }
    },
    testimonial: {
      text: {
        name: palette.gray[500],
        label: palette.gray[600]
      },
      background: palette.gray[50],
      link: palette.gray[700]
    }
  }
};
const colors = { ...palette, ...paletteTokens, ...colorTokens };

const variants$2 = {
  // #region Orium theme
  // token name needs to be exact match
  blockQuote: {
    fontFamily: fonts.body,
    fontWeight: fontWeights.normal,
    lineHeight: lineHeights.base,
    borderLeft: `${spacing[1]} solid ${palette.primary[900]}`,
    marginLeft: spacing[4],
    padding: `0 ${spacing[4]}`,
    color: palette.shading[600],
    fontSize: fontSizes.lg,
    fontStyle: "italic"
  },
  body: {
    fontFamily: fonts.body,
    fontSize: { base: fontSizes.md, lg: fontSizes.lg },
    fontWeight: fontWeights.normal,
    lineHeight: lineHeights.short
  },
  caption: {
    display: "block",
    fontSize: { base: fontSizes.xs, md: fontSizes.sm },
    lineHeight: { base: lineHeights.shorter, md: lineHeights.normal }
  },
  headline: {
    fontFamily: fonts.heading,
    fontSize: { base: fontSizes.md, lg: fontSizes.lg },
    fontWeight: fontWeights.medium,
    lineHeight: lineHeights.base
  },
  subhead: {
    fontWeight: fontWeights.medium,
    lineHeight: lineHeights.base
  },
  tag: {
    height: spacing[6],
    backgroundColor: palette.warning[700],
    color: palette.shading[100],
    textTransform: "uppercase",
    letterSpacing: letterSpacings.wider,
    fontWeight: fontWeights.bold,
    textAlign: "center",
    top: spacing[4],
    right: spacing[4],
    padding: `${spacing[1]} ${spacing[3]}`,
    fontSize: fontSizes.xs
  },
  textHighlight: {
    fontSize: fontSizes.lg,
    lineHeight: lineHeights.base,
    color: palette.primary[900],
    padding: spacing[6]
  },
  // #endregion
  // #region Spoonflower theme
  /**
   * If you're wondering "Why isn't this working!?", you may need to add "min-width: 0"
   * to the parent of the container you set this textStyle on.
   */
  ellipsis: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  heading: {
    fontFamily: "heading",
    fontWeight: "normal",
    letterSpacing: "normal",
    lineHeight: lineHeights.none
  },
  subheading: {
    fontFamily: "heading",
    fontWeight: "light",
    letterSpacing: "wide",
    textTransform: "uppercase"
  },
  "heading-serif": {
    fontFamily: "heading-serif",
    fontWeight: "normal",
    letterSpacing: "normal",
    lineHeight: "shorter"
  },
  p: {
    marginBottom: "4",
    marginTop: "4",
    fontWeight: "normal",
    lineHeight: "short",
    letterSpacings: "normal",
    fontSize: { base: fontSizes.md, lg: fontSizes.lg }
  }
  // #endregion
};
const sizes$2 = {};
const textStyles$1 = { ...sizes$2, ...variants$2 };

const subHeadingStyles = {
  baseStyle: {
    /**
     * What we should be doing here is "textStyle: 'heading'", however:
     * https://github.com/chakra-ui/chakra-ui/issues/3884
     */
    ...textStyles$1["subheading"],
    color: "gray.500",
    fontFamily: "'Figtree', sans-serif",
    fontWeight: "light",
    fontSize: { base: "md", lg: "2xl" }
  }
  // sizes: {
  //   sm: {
  //     fontSize: 'sm',
  //   },
  //   md: {
  //     fontSize: 'md',
  //   },
  //   lg: {
  //     fontSize: 'lg',
  //   },
  //   xl: {
  //     fontSize: 'xl',
  //   },
  //   '2xl': {
  //     fontSize: '2xl',
  //   },
  //   '3xl': {
  //     fontSize: '3xl',
  //   },
  //   '4xl': {
  //     fontSize: '4xl',
  //   },
  // }
};

const headingStyles = {
  baseStyle: {
    /**
     * What we should be doing here is "textStyle: 'heading'", however:
     * https://github.com/chakra-ui/chakra-ui/issues/3884
     */
    ...textStyles$1["heading"],
    lineHeight: lineHeights.none
  },
  sizes: {
    xs: {
      fontSize: "0.875rem"
      // 14px
    },
    sm: {
      fontSize: "1rem"
      // 16px
    },
    md: {
      fontSize: "1.25rem"
      // 20px
    },
    lg: {
      fontSize: "1.5rem"
      // 24px
    },
    xl: {
      fontSize: "1.875rem"
      // 30px
    },
    "2xl": {
      fontSize: "2.25rem"
      // 36px
    },
    "3xl": {
      fontSize: "2.5rem"
      // 40px
    },
    "4xl": {
      fontSize: "3rem"
      // 48px
    },
    "5xl": {
      fontSize: "3.75rem"
      // 60px
    },
    "6xl": {
      fontSize: "4.5rem"
      // 72px
    },
    "7xl": {
      fontSize: "6rem"
      // 96px
    }
  },
  variants: {
    serif: {
      /**
       * What we should be doing here is "textStyle: 'heading'", however:
       * https://github.com/chakra-ui/chakra-ui/issues/3884
       */
      ...textStyles$1["heading"]
    },
    subheading: {
      ...subHeadingStyles.baseStyle,
      sm: {
        fontSize: "0.875rem"
        // 14px
      },
      md: {
        fontSize: "1rem"
        // 16px
      },
      lg: {
        fontSize: "1.125rem"
        // 18px
      },
      xl: {
        fontSize: "1.25rem"
        // 20px
      },
      "2xl": {
        fontSize: "1.5rem"
        // 24px
      },
      "3xl": {
        fontSize: "1.875rem"
        // 30px
      },
      "4xl": {
        fontSize: "2.25rem"
        // 36px
      }
    }
  }
};

const inputStyles = {
  baseStyle: {},
  sizes: {},
  variants: {
    outline: (props) => {
      return {
        field: {
          backgroundColor: "white.50",
          color: "gray.900",
          borderColor: "gray.600",
          _hover: {
            borderColor: "gray.700"
          },
          _focus: {
            borderWidth: "3px"
          },
          _invalid: {
            borderWidth: "2px",
            boxShadow: "none"
          },
          _placeholder: {
            color: "#737D80"
          },
          _readOnly: {
            _focus: {
              boxShadow: "none",
              borderColor: "gray.600",
              borderWidth: "1px"
            }
          }
        },
        addon: {
          color: "teal.500"
        }
      };
    }
  },
  defaultProps: {
    errorBorderColor: "red.500",
    focusBorderColor: "teal.600"
  }
};

const labelStyles = {
  baseStyle: {
    color: "gray.600",
    fontSize: "md",
    fontWeight: "bold",
    lineHeight: "shorter",
    letterSpacing: "wide"
  }
};

const baseStyle$3 = defineStyle({
  transitionProperty: "common",
  transitionDuration: "fast",
  transitionTimingFunction: "ease-out",
  cursor: "pointer",
  textDecoration: "underline",
  outline: "none",
  color: "teal.500",
  _hover: {
    textDecoration: "underline"
  },
  _focusVisible: {
    boxShadow: "outline"
  }
});
const userNavigation = defineStyle({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "sm",
  borderRadius: "md",
  padding: 2.5,
  lineHeight: 5,
  color: "gray.800",
  textDecoration: "none",
  _hover: {
    color: "gray.900",
    bg: "gray.200",
    textDecoration: "underline"
  },
  _active: {
    color: "gray.900",
    bg: "gray.300",
    textDecoration: "underline"
  },
  _focus: {
    color: "gray.900",
    bg: "gray.300",
    textDecoration: "underline"
  }
});
const admin = defineStyle({
  fontSize: "sm",
  bgColor: "teal.500",
  py: 0,
  px: 2,
  color: "white",
  border: "1px solid transparent",
  borderRadius: "md",
  textDecoration: "none",
  _hover: {
    bg: "teal.600",
    textDecoration: "none"
  },
  _active: {
    bg: "teal.700"
  },
  _focus: {
    bg: "teal.700"
  }
});
const header = defineStyle({
  color: "gray.800",
  textDecoration: "none",
  _hover: {
    textDecoration: "underline"
  },
  _focus: {
    textDecoration: "underline"
  },
  _active: {
    textDecoration: "underline"
  }
});
const footer = defineStyle({
  color: "gray.800",
  textDecoration: "none"
});
const externalButton = defineStyle({
  bgColor: "white",
  fontSize: "sm",
  fontWeight: "semibold",
  textDecoration: "none",
  color: "external-link-button-color",
  borderStyle: "solid",
  borderWidth: "1px",
  borderColor: "granite.600",
  borderRadius: "md",
  display: "flex",
  alignItems: "center",
  px: 1.5,
  py: 0.5,
  gap: 1
});
const variants$1 = {
  "user-navigation": userNavigation,
  admin,
  header,
  footer,
  "external-button": externalButton
};
var link = defineStyleConfig({
  baseStyle: baseStyle$3,
  variants: variants$1
});

const modalStyles = {
  baseStyle: {
    body: {
      padding: "0"
    },
    dialog: {
      margin: "4",
      paddingX: {
        base: "6",
        md: "12"
      },
      paddingY: {
        base: "8",
        md: "12"
      }
    },
    footer: {
      padding: "0"
    },
    header: {
      padding: "0"
    }
  },
  variants: {
    confirmation: {
      body: {
        padding: "0"
      },
      dialog: {
        marginX: {
          base: "0",
          md: "4"
        },
        marginY: {
          base: "1",
          md: "4"
        },
        paddingX: {
          base: "4",
          md: "12"
        },
        pt: {
          base: "8",
          md: "12"
        },
        pb: {
          base: "8",
          md: "8"
        }
      },
      footer: {
        padding: "0"
      },
      header: {
        padding: "0"
      }
    }
  }
};

const radioStyles = {
  // Styles for the base style
  baseStyle: {
    control: {
      borderColor: "black",
      _checked: {
        background: "teal.600",
        borderColor: "teal.600",
        _hover: {
          background: "teal.600",
          borderColor: "teal.600"
        }
      },
      _hover: {}
    }
  }
};

const textStyles = {
  textAlign: "start",
  maxWidth: "305px",
  textWrap: "wrap"
};
const baseStyle$2 = {
  table: {
    fontVariantNumeric: "lining-nums tabular-nums",
    borderCollapse: "collapse",
    width: "full"
  },
  th: {
    fontFamily: "heading",
    textTransform: "none",
    ...textStyles,
    "&:first-of-type": {
      position: "sticky",
      left: 0,
      minWidth: "initial"
    }
  },
  td: {
    ...textStyles,
    "&:first-of-type": {
      position: "sticky",
      left: 0,
      backgroundColor: "gray.50",
      border: "1px",
      borderColor: "gray.100"
    }
  },
  caption: {
    mt: 4,
    fontFamily: "heading",
    textAlign: "center",
    fontWeight: "medium"
  }
};
const numericStyles = {
  "&[data-is-numeric=true]": {
    textAlign: "end"
  }
};
const variants = {
  simple: {
    th: {
      color: "gray.100",
      border: "1px",
      borderColor: "gray.100",
      backgroundColor: "gray.50",
      ...numericStyles,
      p: {
        color: "primary.500"
      },
      a: {
        _hover: {
          color: "primary.700"
        },
        textDecoration: "none!important",
        "&:after": {
          content: '"   >"',
          whiteSpace: "pre"
        }
      },
      "&:last-of-type": {
        borderColor: "gray.100"
      }
    },
    tr: {
      color: "gray.100",
      border: "1px",
      borderColor: "gray.100",
      "&:last-of-type": {
        td: {
          borderColor: "gray.100"
        }
      }
    },
    td: {
      border: "1px",
      borderColor: "gray.100",
      minWidth: "44",
      ...numericStyles,
      p: {
        color: `${paletteTokens.colors.text.secondary.base}!important`
      },
      "&:first-of-type": {
        p: {
          color: `${paletteTokens.colors.text.primary.base}!important`
        },
        minWidth: "initial"
      }
    },
    caption: {
      color: "gray.100"
    },
    tfoot: {
      tr: {
        "&:last-of-type": {
          th: { borderBottomWidth: 0 }
        }
      }
    }
  }
};
const sizes$1 = {
  sm: {
    th: {
      px: "3",
      py: "2",
      lineHeight: "4",
      fontSize: "xs"
    },
    td: {
      px: "3",
      py: "2",
      fontSize: "sm",
      lineHeight: "4"
    },
    caption: {
      px: "3",
      py: "2",
      fontSize: "xs"
    }
  },
  md: {
    th: {
      px: "6",
      py: "3",
      lineHeight: "4",
      fontSize: "xs"
    },
    td: {
      px: "6",
      py: "4",
      lineHeight: "5"
    },
    caption: {
      px: "6",
      py: "2",
      fontSize: "sm"
    }
  },
  lg: {
    th: {
      px: "8",
      py: "4",
      lineHeight: "5",
      fontSize: "sm"
    },
    td: {
      px: "8",
      py: "5",
      lineHeight: "6"
    },
    caption: {
      px: "6",
      py: "2",
      fontSize: "md"
    }
  }
};
const Table = defineStyleConfig({
  baseStyle: baseStyle$2,
  variants,
  sizes: sizes$1,
  defaultProps: {
    variant: "simple",
    size: "md",
    colorScheme: "gray"
  }
});

const tabStyles = {
  baseStyle: {
    tab: {
      // This is for the "ellipsis" textStyle to work correctly inside a tab.
      minWidth: "0",
      _selected: {
        backgroundColor: "white.50"
      }
    },
    tablist: {
      position: "relative",
      zIndex: "1"
    }
  },
  sizes: {
    // TODO[SFP1-338]: "sm" hasn't been properly defined in the theme, so these values are just a placeholder.
    sm: {
      tab: {
        fontSize: "sm",
        height: "1.75rem",
        paddingX: "2",
        paddingY: "1"
      }
    },
    md: {
      tab: {
        fontSize: "md",
        height: "2rem",
        paddingX: "4",
        paddingY: "1"
      }
    },
    // TODO[SFP1-338]: "lg" hasn't been properly defined in the theme, so these values are just a placeholder.
    lg: {
      tab: {
        fontSize: "lg",
        height: "2.25rem",
        paddingX: "4",
        paddingY: "2"
      }
    }
  },
  variants: {
    enclosed: (props) => {
      const style = Tabs.variants?.enclosed(props);
      const { colorScheme } = props;
      return {
        ...style,
        tab: {
          ...style?.tab,
          color: `${colorScheme}.600`,
          filter: "saturate(0)",
          _selected: {
            ...style?.tab._selected,
            color: `${colorScheme}.900`,
            filter: "saturate(1)",
            borderColor: `${colorScheme}.300`,
            borderBottomColor: "white.50"
          }
        }
      };
    }
  }
};

const tagStyles = {
  baseStyle: () => ({
    container: {
      fontWeight: "bold",
      letterSpacing: "wider",
      padding: "2px 4px",
      borderRadius: "2px",
      fontSize: "12px",
      gap: "4px"
    }
  }),
  variants: {
    solid: () => {
      return {
        container: {
          bg: "primary",
          color: "white"
        }
      };
    },
    "solid-green": () => {
      return {
        container: {
          bg: "success.100",
          color: "success.500"
        }
      };
    },
    "solid-orange": () => {
      return {
        container: {
          bg: "warning.100",
          color: "warning.500"
        }
      };
    },
    "solid-gray": () => {
      return {
        container: {
          bg: "muted",
          color: "text-muted"
        }
      };
    },
    outline: () => {
      return {
        container: {
          bg: "white",
          color: "primary",
          borderColor: "primary"
        }
      };
    },
    subtle: () => {
      return {
        container: {
          bg: "highlight",
          color: "primary"
        }
      };
    }
  }
};

const styles$1 = {
  baseStyle: {
    fontWeight: "normal",
    letterSpacing: "normal",
    lineHeight: "short"
  },
  sizes: {
    xs: {
      fontSize: "xs"
    },
    sm: {
      fontSize: "sm"
    },
    md: {
      fontSize: "md"
    },
    lg: {
      fontSize: "lg"
    },
    xl: {
      fontSize: "xl"
    },
    "2xl": {
      fontSize: "2xl"
    },
    "3xl": {
      fontSize: "3xl"
    },
    "4xl": {
      fontSize: "4xl"
    },
    "5xl": {
      fontSize: "5xl"
    },
    "6xl": {
      fontSize: "6xl"
    },
    "7xl": {
      fontSize: "7xl"
    },
    "8xl": {
      fontSize: "8xl"
    },
    "9xl": {
      fontSize: "9xl"
    }
  },
  variants: {
    p: {
      /**
       * What we should be doing here is "textStyle: 'p'", however:
       * https://github.com/chakra-ui/chakra-ui/issues/3884
       */
      ...textStyles$1.p
    }
  }
};

const $arrowBg = cssVar("popper-arrow-bg");
const tooltipStyles = {
  baseStyle: {
    padding: 2,
    textAlign: "center",
    borderRadius: "md"
  },
  variants: {
    dark: {
      backgroundColor: "gray.900",
      color: "white",
      [$arrowBg.variable]: "colors.gray.900"
    },
    light: {}
  }
};

var components = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Alert: alertStyles,
  AlertDialog: alertDialogStyle,
  Badge: badgeStyle,
  Button: Button,
  Card: cardStyles,
  Checkbox: checkboxStyle,
  Divider: dividerStyles,
  FormLabel: labelStyles,
  Heading: headingStyles,
  Input: inputStyles,
  Link: link,
  Modal: modalStyles,
  Radio: radioStyles,
  Subheading: subHeadingStyles,
  Table: Table,
  Tabs: tabStyles,
  Tag: tagStyles,
  Text: styles$1,
  Tooltip: tooltipStyles
});

const { definePartsStyle: definePartsStyle$1, defineMultiStyleConfig: defineMultiStyleConfig$1 } = createMultiStyleConfigHelpers(drawerAnatomy.keys);
const baseStyle$1 = definePartsStyle$1({
  overlay: {
    bg: "blackAlpha.200"
  },
  header: {
    paddingTop: "10",
    fontSize: "2xl",
    fontFamily: "heading",
    fontWeight: "normal"
  },
  dialog: {
    borderRadius: "0",
    bg: "warm-white",
    fontSize: "md"
  }
});
const drawerTheme = defineMultiStyleConfig$1({
  baseStyle: baseStyle$1
});

const breakpoints = {
  ...chakraTheme.breakpoints,
  xs: "20rem",
  // 320px
  sm: "30rem",
  // 480px
  md: "48rem",
  // 768px
  lg: "64rem",
  // 1024px
  xl: "80rem",
  // 1280px
  "2xl": "96rem",
  // 1536px
  "3xl": "108rem"
  // 1728px
};

const largeSizes = {
  max: "max-content",
  min: "min-content",
  full: "100%",
  xs: "20rem",
  // 320px
  sm: "24rem",
  // 384px
  md: "28rem",
  // 448px
  lg: "32rem",
  // 512px
  xl: "36rem",
  // 576px
  "2xl": "42rem",
  // 672px
  "3xl": "48rem",
  // 768px
  "4xl": "56rem",
  // 896px
  "5xl": "64rem",
  // 1024px
  "6xl": "72rem",
  // 1152px
  "7xl": "80rem",
  // 1280px
  "8xl": "90rem",
  // 1440px
  // '9xl': '103rem', // 1648px + 40px Left and 40px Right = 1728px max width - TBD inclusion in Figma design
  prose: "60ch"
  // 960px
};
const sizes = {
  ...chakraTheme.sizes,
  ...spacing,
  ...largeSizes,
  container: breakpoints
};

const { definePartsStyle, defineMultiStyleConfig } = createMultiStyleConfigHelpers(menuAnatomy.keys);
const baseStyle = definePartsStyle({
  list: {
    border: "1px solid",
    borderColor: "gray.100",
    borderRadius: "sm",
    maxHeight: `calc(${sizes["2xl"]} + ${spacing[5]})`,
    backgroundColor: "white",
    paddingY: 0,
    overflowY: "auto",
    marginTop: `-${spacing["px"]}`
  },
  item: {
    fontSize: "sm",
    color: "text",
    fontWeight: "light",
    lineHeight: "shorter",
    minHeight: 10,
    background: "transparent",
    _hover: {
      backgroundColor: "gray.50"
    },
    "@media (hover: none)": {
      _hover: {
        outline: "none"
      },
      _active: {
        backgroundColor: "transparent"
      },
      _focus: {
        boxShadow: "outline",
        backgroundColor: "transparent"
      }
    },
    _focusVisible: {
      boxShadow: "outline",
      backgroundColor: "gray.50"
    },
    marginX: 0
  }
});
const menuTheme = defineMultiStyleConfig({ baseStyle });

const borders = {
  ...chakraTheme.borders,
  none: 0,
  "1px": "1px solid",
  "2px": "2px solid",
  "4px": "4px solid",
  "8px": "8px solid"
};

const radii = {
  ...chakraTheme.radii,
  none: "0",
  sm: "0.125rem",
  //2px
  base: "0.25rem",
  //4px
  md: "0.375rem",
  //6px
  lg: "0.5rem",
  //8px
  xl: "0.75rem",
  //12px
  "2xl": "1rem",
  //16px
  "3xl": "1.5rem",
  //24px
  full: "9999px"
  //9999px
};

const shadows = {
  ...chakraTheme.shadows,
  xs: "0px 0px 0px 1px rgba(0, 0, 0, 0.05)",
  sm: "0px 1px 2px 0px rgba(0, 0, 0, 0.05)",
  base: "0px 1px 2px 0px rgba(0, 0, 0, 0.06), 0px 1px 3px 0px rgba(0, 0, 0, 0.1)",
  md: "0px 2px 4px -1px rgba(0, 0, 0, 0.06), 0px 4px 6px -1px rgba(0, 0, 0, 0.1)",
  lg: "0px 4px 6px -2px rgba(0, 0, 0, 0.05), 0px 10px 15px -3px rgba(0, 0, 0, 0.1)",
  xl: "0px 10px 10px -5px rgba(0, 0, 0, 0.04), 0px 20px 25px -5px rgba(0, 0, 0, 0.1)",
  "2xl": "0px 25px 50px -12px rgba(0, 0, 0, 0.25)",
  outline: "0 0 0 3px rgba(66, 153, 225, 0.6)",
  inner: "inset 0 2px 4px 0 rgba(0,0,0,0.06)",
  none: "none",
  "dark-lg": "0px 15px 40px 0px rgba(0, 0, 0, 0.4), 0px 5px 10px 0px rgba(0, 0, 0, 0.2)"
};

const foundations = {
  ...typography,
  borders,
  breakpoints,
  colors,
  radii,
  shadows,
  sizes,
  space: spacing
};

const semanticTokens = {
  colors: {
    // #region Orium theme
    // Figma tokens
    background: {
      default: `${paletteTokens.colors.light["background"]} !important`,
      _dark: `${paletteTokens.colors.light["background"]} !important`
    },
    text: {
      default: paletteTokens.colors.light["text"],
      _dark: paletteTokens.colors.dark["text"]
    },
    "text-muted": {
      default: paletteTokens.colors.light["text-muted"],
      _dark: paletteTokens.colors.dark["text-muted"]
    },
    primary: {
      default: paletteTokens.colors.light["primary"],
      _dark: paletteTokens.colors.dark["primary"]
    },
    secondary: {
      default: paletteTokens.colors.light["secondary"],
      _dark: paletteTokens.colors.dark["secondary"]
    },
    highlight: {
      default: paletteTokens.colors.light["highlight"],
      _dark: paletteTokens.colors.dark["highlight"]
    },
    muted: {
      default: paletteTokens.colors.light["muted"],
      _dark: paletteTokens.colors.dark["muted"]
    },
    accent: {
      default: paletteTokens.colors.light["accent"],
      _dark: paletteTokens.colors.dark["accent"]
    },
    // Custom tokens (not in Figma)
    "bg-danger": {
      default: "danger.500",
      _dark: "danger.900"
    },
    "bg-success": {
      default: "success.500",
      _dark: "success.900"
    },
    "bg-btn-muted": {
      default: paletteTokens.colors.light["muted"],
      _dark: paletteTokens.colors.dark["muted"]
    },
    "fg-btn-muted": {
      default: paletteTokens.colors.light["text"],
      _dark: paletteTokens.colors.dark["text-muted"]
    },
    // #endregion
    // #region Spoonflower theme
    "link-text": {
      _dark: "white-opacity.500",
      _light: "teal.500"
    },
    "link-text-hover": {
      _dark: "white-opacity.700",
      _light: "teal.700"
    },
    "primary-text": {
      _dark: "white-opacity.900",
      _light: "gray.900"
    },
    "secondary-text": {
      _dark: "white-opacity.700",
      _light: "gray.700"
    },
    "tertiary-text": {
      _dark: "white-opacity.500",
      _light: "gray.500"
    }
    // #endregion
  }
};

const styles = {
  global: (props) => ({
    html: {
      scrollBehavior: "smooth"
    },
    body: {
      [`@media (prefers-color-scheme: dark)`]: {
        background: "white !important",
        color: "inherit !important"
      }
    },
    ".visually-hidden:not(:focus):not(:active)": {
      clip: "rect(0 0 0 0)",
      clipPath: "inset(50%)",
      height: "1px",
      overflow: "hidden",
      position: "absolute",
      whiteSpace: "nowrap",
      width: "1px"
    },
    ".visually-hidden": {
      _focus: {
        outlineStyle: "solid",
        outlineWidth: "3px",
        outlineColor: "gray.900",
        padding: ".5rem",
        position: "absolute",
        zIndex: "1"
      }
    }
  })
};

const extendedTheme = {
  config: {
    initialColorMode: "light",
    useSystemColorMode: false,
    cssVarPrefix: "chakra"
  },
  ...foundations,
  textStyles: textStyles$1,
  // text variants
  layerStyles,
  // style variants
  styles,
  // Global style override
  colors,
  semanticTokens,
  components: {
    ...components,
    Menu: menuTheme,
    Drawer: drawerTheme
  }
};
const theme = extendTheme(extendedTheme);
const useTheme = () => useTheme$1();

export { theme, useTheme };
